import { useState } from 'react';
import type { StrategicPillar } from '../../../types/audit';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

interface StrategyPillarProps {
  pillar: StrategicPillar;
  index: number;
}

/**
 * Collapsible strategic pillar in the design prototype's steps-list idiom:
 * an ink number circle, and inside the expanded plan each phase leads with an
 * orange mono "when" eyebrow (`.stp__when`) over its actions.
 */
export default function StrategyPillar({ pillar, index }: StrategyPillarProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card>
      <CardHeader
        className="cursor-pointer select-none"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3.5">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary font-mono text-sm font-semibold text-primary-foreground">
            {index + 1}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold tracking-[-0.01em] text-foreground">{pillar.title}</div>
            <div className="mt-0.5 text-xs leading-snug text-muted-foreground">{pillar.objective}</div>
          </div>
          <div className="hidden items-center gap-5 sm:flex">
            <div className="text-right">
              <div className="text-sm font-semibold tabular-nums text-foreground">{pillar.investment}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">Investment</div>
            </div>
            <div className="text-right">
              <div className="text-sm font-semibold tabular-nums text-foreground">{pillar.expectedReturn}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">Return</div>
            </div>
          </div>
          <svg
            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round"
            className={cn(
              'shrink-0 transition-transform duration-200',
              expanded && 'rotate-180'
            )}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="flex flex-col gap-4 pt-0">
          <div className="rounded-md bg-muted/50 px-3.5 py-2.5">
            <div className="text-sm font-semibold text-foreground">{pillar.kpiTarget}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">KPI target</div>
          </div>
          <div className="flex flex-col gap-2">
            {pillar.phases.map((phase) => (
              <div
                key={phase.phase}
                className="rounded-xl border border-border bg-card px-4 py-3.5"
              >
                <div className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-[0.07em] text-secondary">
                  {phase.label || phase.phase}
                </div>
                <div className="text-sm font-semibold tracking-[-0.01em] text-foreground">{phase.phase}</div>
                <div className="mt-1.5 flex flex-col gap-1.5">
                  {phase.actions.map((action, i) => (
                    <div className="flex items-start gap-2 text-[12.5px] leading-normal text-muted-foreground" key={i}>
                      <svg className="mt-0.5 shrink-0 text-secondary" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {action}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
